import { useEffect, useState, useCallback, useRef } from "react";
import type { BookingDraft, BuildingType } from "../../shared/types.js";

const STORAGE_KEY = "aag_booking_draft_v1";

export interface BookingWizardState extends BookingDraft {
  step: number;
  holdId: string | null;
  idempotencyKey: string;
}

function randomId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultState(): BookingWizardState {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const buildingType = (params?.get("buildingType") as BuildingType) || "appartement";
  const surfaceM2 = Number(params?.get("surfaceM2")) || 100;
  const buildYear = Number(params?.get("buildYear")) || 1975;
  const floors = Number(params?.get("floors")) || 1;
  const hasBasement = params?.get("hasBasement") === "true";

  return {
    step: 1,
    building: {
      buildingType,
      floors,
      hasBasement,
      hasOutbuildings: false,
      surfaceM2,
      buildYear,
      units: 1,
      notes: "",
    },
    service: { addOns: [], extraSamples: 0, urgent: false },
    contact: {
      firstName: "",
      lastName: "",
      email: "",
      emailConfirm: "",
      phone: "",
      street: "",
      houseNumber: "",
      bus: "",
      postalCode: "",
      city: "",
      billingDifferent: false,
      billingAddress: "",
      vatNumber: "",
      notes: "",
    },
    slot: null,
    acceptedTerms: false,
    acceptedPrivacy: false,
    marketingConsent: false,
    holdId: null,
    idempotencyKey: randomId(),
  };
}

export function useBookingDraft() {
  const [state, setState] = useState<BookingWizardState>(() => {
    if (typeof window === "undefined") return defaultState();
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaultState(), ...parsed, idempotencyKey: parsed.idempotencyKey || randomId() };
      }
    } catch {
      /* corrupte opslag negeren, van nul starten */
    }
    return defaultState();
  });

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* localStorage kan uitgeschakeld/vol zijn — boeking blijft werken, enkel geen persistente state */
    }
  }, [state]);

  const update = useCallback((patch: Partial<BookingWizardState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
    setState(defaultState());
  }, []);

  return { state, update, reset };
}
